import { useCallback, useContext, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { WebsocketEvent } from "@devour/client";
import { WebsocketProviderContext } from "@/components/WebsocketProvider";
import useWebsocket from "@/hooks/useWebsocket";
import { SCORING_HEALTH_QUERY_KEY } from "./useScoringHealth";

/*
 * Prize pool WebSocket event - defined in packages/models/src/WebsocketEvent.ts
 * but not yet regenerated in @devour/client. Using string literal until client is regenerated.
 */
const LEADERBOARD_PRIZE_POOL_UPDATE = "LEADERBOARD_PRIZE_POOL_UPDATE" as WebsocketEvent;

/** Coalesce prize-tier refetches so a burst of joins costs one request, not one each. */
const TIERS_REFETCH_DEBOUNCE_MS = 1500;

/**
 * Standing data included in WebSocket broadcast
 */
interface LeaderboardStandingData {
    rank: number;
    userId: string;
    displayName: string;
    avatarUrl?: string;
    value: number;
    eventCount: number;
    previousRank?: number;
}

/**
 * Payload for leaderboard standings update broadcast
 */
interface LeaderboardStandingsUpdatePayload {
    leaderboardId: string;
    updatedAt: number;
    standings?: LeaderboardStandingData[];
    totalParticipants?: number;
}

/**
 * Payload for personal score update (sent to user's room)
 * Contains data to patch my-rank cache directly - ZERO API calls!
 */
interface CompetitionScoreUpdatePayload {
    leaderboardId: string;
    pointsAdded: number;
    eventsAdded: number;
    rank: number | null;
    value: number | null;
    eventCount: number | null;
    inTop100: boolean;
}

/**
 * My rank cache data structure
 */
interface MyRankCacheData {
    isParticipating: boolean;
    rank: number | null;
    value: number;
    eventCount: number;
    inTop100?: boolean;
    manualFetchTime?: number;
}

/**
 * Payload for prize pool update broadcast (when someone joins with entry fee)
 * Matches the broadcast structure from LeaderboardService.joinLeaderboard()
 *
 * For USD_EQUIVALENT mode: totalValue is in USD
 * For Fuel mode: totalValue is in Fuel
 */
interface LeaderboardPrizePoolUpdatePayload {
    leaderboardId: string;
    /** Display value (USD or Fuel depending on mode) */
    totalValue: number;
    /** Amount available for winners (same as totalValue) */
    winnerPool: number;
    /** Total Fuel collected (before platform fee) */
    currentPool: number;
    /** Number of paid entries */
    entryCount: number;
    /** Entry fee per participant */
    entryFee: number;
    /** "USD" or "FUEL" */
    currency: string;
    /** Whether this is USD_EQUIVALENT mode */
    isUsdMode: boolean;
    /** Timestamp of update */
    updatedAt: number;
}

/**
 * WebSocket hook for leaderboard real-time updates
 *
 * This hook provides true real-time updates by:
 * 1. Joining a leaderboard-specific room to receive broadcasts
 * 2. Receiving full standings data in WebSocket payload (no API refetch!)
 * 3. Using setQueriesData to patch ALL matching cache entries
 *
 * The backend broadcasts LEADERBOARD_STANDINGS_UPDATE with standings data
 * to all viewers in the leaderboard room whenever any participant's score changes.
 *
 * @param leaderboardId - The actual MongoDB ObjectId of the leaderboard (for room joins)
 * @param cacheKeyId - The URL param (ID or slug) used in React Query cache keys
 * @param enabled - Whether to enable the WebSocket subscription (default: true)
 */
export function useLeaderboardWebSocket(
    leaderboardId: string | undefined,
    cacheKeyId: string | undefined,
    enabled: boolean = true,
): void {
    const queryClient = useQueryClient();
    const { socket, socketConnection } = useContext(WebsocketProviderContext);
    const tiersRefetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    /* The board already joined once — a second join for it is a reconnect. */
    const joinedBoardRef = useRef<string | undefined>(undefined);

    // Don't fire a queued tier refetch after the consumer has gone away.
    useEffect(() => () => {
        if (tiersRefetchTimer.current) {
            clearTimeout(tiersRefetchTimer.current);
        }
    }, []);

    /**
     * Join/leave leaderboard room for real-time broadcasts
     * Uses the actual ObjectId for room name (validated by backend)
     */
    useEffect(() => {
        if (!socket || !socketConnection || !leaderboardId || !enabled) {
            return;
        }

        // Join the leaderboard room using actual ObjectId
        socket.emit("leaderboard:join", leaderboardId);
        console.log(`[LeaderboardWebSocket] Joined room for ${leaderboardId}`);

        /*
         * REPAIR ON REJOIN. Broadcasts are fire-and-forget — anything sent while
         * this client was disconnected is gone — and a phone drops its socket the
         * moment the app backgrounds. Without this the board sat on stale
         * standings until something else happened to refetch, which is how a
         * member ends up closing and reopening the app to see points they had
         * already earned. Only on a REJOIN: the first join coincides with the
         * queries' own first request, so asking then would just duplicate it.
         */
        const keyForCache = cacheKeyId || leaderboardId;
        const isRejoin = joinedBoardRef.current === leaderboardId;
        joinedBoardRef.current = leaderboardId;
        if (isRejoin) {
            queryClient.invalidateQueries({queryKey: ["leaderboard-standings", keyForCache]});
            queryClient.invalidateQueries({queryKey: ["leaderboard-my-rank", keyForCache]});
        }

        // Cleanup: leave the room when unmounting or dependencies change
        return () => {
            socket.emit("leaderboard:leave", leaderboardId);
            console.log(`[LeaderboardWebSocket] Left room for ${leaderboardId}`);
        };
    }, [socket, socketConnection, leaderboardId, enabled, cacheKeyId, queryClient]);

    /**
     * Handler for broadcast standings updates (from any user's score change)
     * Patches cache directly with received data - NO API CALL
     */
    const handleStandingsUpdate = useCallback((data: LeaderboardStandingsUpdatePayload) => {
        // Only update if this is for our leaderboard
        if (!leaderboardId || data?.leaderboardId !== leaderboardId) {
            return;
        }

        const keyForCache = cacheKeyId || leaderboardId;

        /*
         * If standings data is included, patch ALL matching cache entries directly (zero API calls!)
         * setQueriesData matches partial keys, unlike setQueryData which needs exact match
         */
        if (data.standings && data.standings.length > 0) {
            console.log(`[LeaderboardWebSocket] Patching cache with ${data.standings.length} standings`);

            /*
             * MERGE per row, never replace wholesale. The broadcast is a slim
             * TopEntryData: no eventCountsByType, no lastEventAt, no
             * subscriptionTier, no isMe, no stream. The table renders the stat
             * cells, the breakdown caret, the tier ring, the "You" tag and the
             * live badge from exactly those. A wholesale swap drops all of them
             * for the beat until the refetch lands, which is the flashing.
             *
             * 🔴 Matched on displayName, NOT userId. This merge used to key on
             * `userId` and could never fire: the broadcast strips it as PII
             * (handleMessage's roomStandings), and the REST rows it merges onto
             * only carry it for the viewer's OWN row (`isMe && s.userId`). Two
             * independent redactions, so the key was absent on both sides at
             * once and every scoring event took the replace path. displayName is
             * the only field the two shapes share.
             *
             * A name that appears more than once on the page is left out of the
             * map rather than guessed at — merging someone else's stats onto a
             * namesake is worse than the row it would have fixed.
             */
            queryClient.setQueriesData(
                { queryKey: ["leaderboard-standings", keyForCache] },
                (oldData: { standings: LeaderboardStandingData[]; total?: number } | undefined) => {
                    if (!oldData) return oldData; // Don't create cache entries that don't exist
                    const seen = new Map<string, number>();
                    for (const s of oldData.standings ?? []) {
                        if (s.displayName) seen.set(s.displayName, (seen.get(s.displayName) ?? 0) + 1);
                    }
                    const prevByName = new Map<string, LeaderboardStandingData>();
                    for (const s of oldData.standings ?? []) {
                        if (s.displayName && seen.get(s.displayName) === 1) prevByName.set(s.displayName, s);
                    }
                    return {
                        ...oldData,
                        standings: data.standings!.map(s => {
                            const prev = s.displayName ? prevByName.get(s.displayName) : undefined;
                            /*
                             * previousRank is broadcast-owned: present only on the
                             * broadcast where the rank moved. Spreading prev under s
                             * would let an old arrow outlive the move — pin it to
                             * exactly what this broadcast says.
                             */
                            return prev ? { ...prev, ...s, previousRank: s.previousRank } : s;
                        }),
                        ...data.totalParticipants !== undefined ? { total: data.totalParticipants } : {},
                    };
                },
            );

            /*
             * Guarantee the render updates. The optimistic patch above only
             * lands when a matching cache entry already exists AND its shape
             * matches; the retheme introduced a separate useGetLeaderboardStandings
             * hook, so also invalidate to force a refetch of whatever standings
             * query is actually mounted. Patch = instant; invalidate = correct.
             */
            queryClient.invalidateQueries({
                queryKey: ["leaderboard-standings", keyForCache],
            });

            /*
             * No board-count patch here: a score push carries the scoring field size, not the shown
             * occurrence's registrants. Joins and cancels send count-only pushes, handled below.
             */
        } else {
            /*
             * No standings rows: the legacy refetch-only broadcast, or a join/cancel saying the
             * player count moved — the only thing between another viewer and the board's 5-minute
             * staleTime. Refetch, not patch — the pushed number is the count of the occurrence that was
             * joined, which need not be the one on screen. Debounced with the tiers refetch: a join burst would refetch once per push per viewer.
             */
            if (data.totalParticipants !== undefined) {
                if (tiersRefetchTimer.current) {
                    clearTimeout(tiersRefetchTimer.current);
                }
                tiersRefetchTimer.current = setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: ["leaderboard", keyForCache] });
                }, TIERS_REFETCH_DEBOUNCE_MS);
            }
            queryClient.invalidateQueries({
                queryKey: ["leaderboard-standings", keyForCache],
            });
        }
    }, [leaderboardId, cacheKeyId, queryClient]);

    /**
     * Handler for personal score updates (sent to user's personal room)
     * Patches my-rank cache directly - ZERO API calls!
     *
     * If user is in top 100: uses exact rank/value/eventCount from backend
     * If user is outside top 100: increments value/eventCount, sets rank to null
     */
    const handleScoreUpdate = useCallback((data: CompetitionScoreUpdatePayload) => {
        // Only update if this is for our leaderboard
        if (!leaderboardId || data?.leaderboardId !== leaderboardId) {
            return;
        }

        const keyForCache = cacheKeyId || leaderboardId;

        // Patch my-rank cache directly - no API call needed!
        queryClient.setQueriesData(
            { queryKey: ["leaderboard-my-rank", keyForCache] },
            (oldData: MyRankCacheData | undefined) => {
                if (!oldData) return oldData;

                // Check if we're in the 30-second protection window for manual fetch
                const isProtected = oldData.manualFetchTime &&
                    Date.now() - oldData.manualFetchTime < 30000;

                if (data.inTop100) {
                    /*
                     * User is in top 100 - use exact data from backend
                     * Defensive fallback: if rank is null despite inTop100, keep existing rank
                     */
                    console.log(`[LeaderboardWebSocket] Patching my-rank with top 100 data: rank=${data.rank}, value=${data.value}`);
                    return {
                        ...oldData,
                        isParticipating: true,
                        rank: data.rank ?? oldData.rank,
                        value: data.value ?? oldData.value,
                        eventCount: data.eventCount ?? oldData.eventCount,
                        inTop100: true,
                    };
                }
                // User is outside top 100 - increment values, rank is null (unless protected)
                console.log(`[LeaderboardWebSocket] Patching my-rank with increments: +${data.pointsAdded} points, protected=${isProtected}`);
                return {
                    ...oldData,
                    isParticipating: true,
                    // Keep existing rank if in protection window, otherwise null
                    rank: isProtected ? oldData.rank : null,
                    value: oldData.value + data.pointsAdded,
                    eventCount: oldData.eventCount + data.eventsAdded,
                    inTop100: false,
                    // Preserve manualFetchTime if it exists
                    ...oldData.manualFetchTime ? { manualFetchTime: oldData.manualFetchTime } : {},
                };

            },
        );

        /*
         * Guarantee the render updates regardless of cache-key/shape match —
         * the retheme's useGetMyLeaderboardRank may not align with the
         * optimistic patch above. Patch = instant; invalidate = correct.
         */
        queryClient.invalidateQueries({
            queryKey: ["leaderboard-my-rank", keyForCache],
        });
    }, [leaderboardId, cacheKeyId, queryClient]);

    /**
     * Handler for leaderboard-specific progression events (legacy)
     * Kept for backwards compatibility - will refetch
     */
    const handleLeaderboardProgressed = useCallback(() => {
        if (!leaderboardId) return;

        const keyForCache = cacheKeyId || leaderboardId;
        console.log("[LeaderboardWebSocket] Legacy progression event, refetching");
        queryClient.invalidateQueries({
            queryKey: ["leaderboard-standings", keyForCache],
        });
        queryClient.invalidateQueries({
            queryKey: ["leaderboard-my-rank", keyForCache],
        });
    }, [leaderboardId, cacheKeyId, queryClient]);

    /**
     * The pipeline threw this player's game data away — wrong client for this
     * board. Only my-rank is invalidated: the standings cannot have changed,
     * because nothing scored. Scoped to the board the drop names, so a drop on
     * another board does not refetch this one.
     */
    const handleSourceRejected = useCallback((payload: { competitionId?: string }) => {
        if (!leaderboardId) return;
        if (payload?.competitionId && payload.competitionId !== leaderboardId) return;

        queryClient.invalidateQueries({
            queryKey: ["leaderboard-my-rank", cacheKeyId || leaderboardId],
        });
        /* The scoring-health row's client chip reads the same verdict. */
        queryClient.invalidateQueries({
            queryKey: [SCORING_HEALTH_QUERY_KEY, cacheKeyId || leaderboardId],
        });
    }, [leaderboardId, cacheKeyId, queryClient]);

    /*
     * A field the mode chip reads changed for this player. The payload is only
     * a nudge — the row refetches so the verdict has one implementation.
     */
    const handleScoringHealthChanged = useCallback(() => {
        if (!leaderboardId) return;
        queryClient.invalidateQueries({
            queryKey: [SCORING_HEALTH_QUERY_KEY, cacheKeyId || leaderboardId],
        });
    }, [leaderboardId, cacheKeyId, queryClient]);

    /**
     * Handler for prize pool updates (when someone joins with entry fee)
     * Patches prizePool in leaderboard cache directly - ZERO API calls!
     *
     * The backend sends totalValue in the correct format:
     * - USD_EQUIVALENT mode: totalValue is in USD
     * - Fuel mode: totalValue is in Fuel
     */
    const handlePrizePoolUpdate = useCallback((data: LeaderboardPrizePoolUpdatePayload) => {
        // Only update if this is for our leaderboard
        if (!leaderboardId || data?.leaderboardId !== leaderboardId) {
            return;
        }

        const keyForCache = cacheKeyId || leaderboardId;
        const displayCurrency = data.isUsdMode ? "USD" : "Fuel";
        console.log(`[LeaderboardWebSocket] Prize pool update: ${data.entryCount} entries, pool=${data.totalValue} ${displayCurrency}`);

        // Patch the leaderboard cache with updated prize pool data
        queryClient.setQueriesData(
            { queryKey: ["leaderboard", keyForCache] },
            (oldData: { leaderboard: { prizePool?: Record<string, unknown>; totalParticipants?: number } } | undefined) => {
                if (!oldData?.leaderboard) return oldData;
                /*
                 * `entryCount` is NOT the board's player count and no longer
                 * writes it. It counts entries in ONE occurrence's escrow, so a
                 * reservation for a future night stamped that night's number
                 * onto the board people are looking at. The count-only
                 * standings broadcast triggers the refetch instead.
                 */
                return {
                    ...oldData,
                    leaderboard: {
                        ...oldData.leaderboard,
                        prizePool: {
                            ...oldData.leaderboard.prizePool,
                            totalValue: data.totalValue,
                            winnerPool: data.winnerPool,
                            currency: data.currency,
                            entryCount: data.entryCount,
                            isUsdMode: data.isUsdMode,
                            updatedAt: data.updatedAt,
                        },
                    },
                };
            },
        );

        // New participant joined — refetch standings so they appear immediately
        queryClient.invalidateQueries({ queryKey: ["leaderboard-standings", keyForCache] });

        /*
         * The patch above moves pool totals but not `prizePool.tiers`, so the
         * per-place amounts would sit frozen under a climbing header. Refetch the
         * server-computed split rather than duplicating payout math here.
         *
         * Debounced: a join burst would otherwise refetch this endpoint once per
         * broadcast for every connected viewer.
         */
        if (tiersRefetchTimer.current) {
            clearTimeout(tiersRefetchTimer.current);
        }
        tiersRefetchTimer.current = setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ["leaderboard", keyForCache] });
        }, TIERS_REFETCH_DEBOUNCE_MS);
    }, [leaderboardId, cacheKeyId, queryClient]);

    /*
     * `useWebsocket`'s third argument is `authRequired`, not `enabled`. Standings,
     * progression and prize pool are all public board data, so they pass `false`
     * and rely on room membership for gating; only the personal score update is
     * addressed to a user's own room and needs an account.
     */
    useWebsocket<LeaderboardStandingsUpdatePayload>(
        WebsocketEvent.LEADERBOARD_STANDINGS_UPDATE,
        handleStandingsUpdate,
        false,
    );

    useWebsocket<CompetitionScoreUpdatePayload>(
        WebsocketEvent.COMPETITION_SCORE_UPDATE,
        handleScoreUpdate,
        true,
    );

    useWebsocket<void>(
        WebsocketEvent.LEADERBOARD_PROGRESSED,
        handleLeaderboardProgressed,
        false,
    );

    /*
     * The pipeline discarded this player's game data because it arrived on a
     * client the board does not accept.
     *
     * Refetching my-rank is the whole handler: the server decides whether to
     * say anything and returns `unscoredSourceLabel` when it does, so the
     * message lives in one place rather than being re-derived here.
     *
     * It needs a push because nothing else would arrive — the board's usual
     * refresh is a score change, and this player's score is exactly what is
     * not changing. Addressed to the user's own room, so it needs an account.
     */
    useWebsocket<{ competitionId?: string }>(
        WebsocketEvent.COMPETITION_SOURCE_REJECTED,
        handleSourceRejected,
        true,
    );

    useWebsocket<{ gameId?: string }>(
        WebsocketEvent.SCORING_HEALTH_CHANGED,
        handleScoringHealthChanged,
        true,
    );

    useWebsocket<LeaderboardPrizePoolUpdatePayload>(
        LEADERBOARD_PRIZE_POOL_UPDATE,
        handlePrizePoolUpdate,
        false,
    );
}

export default useLeaderboardWebSocket;
